import { IAuthResponse, IUserProfile } from "@dl/declarations";
import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  PayloadAction,
} from "@reduxjs/toolkit";
import api from "../../api";
import {
  IGoogleSignInDto,
  ISetPasswordDto,
  ITraditionalSignInDto,
} from "../../interfaces/dto.interface";

interface IUserState {
  profile: IUserProfile;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const initialState: IUserState = {
  profile: {
    _id: "",
    name: "",
    email: "",
    title: "",
    permissions: [],
    isPasswordSet: false,
  },

  isLoading: false,
  isAuthenticated: false,
};

export const verifyTokenAsyncAction = createAsyncThunk<IAuthResponse, void>(
  "user/verifyToken",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.put<IAuthResponse>(`/auth/verify`);

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const signOutAsyncAction = createAsyncThunk<void, void>(
  "user/signOut",
  async (_, { rejectWithValue }) => {
    try {
      await api.put(`/auth/sign-out`);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const googleSignInAsyncAction = createAsyncThunk<
  IAuthResponse,
  IGoogleSignInDto
>("user/googleSignIn", async (dto, { rejectWithValue }) => {
  try {
    const response = await api.put<IAuthResponse>(`/auth/sign-in/google`, {
      token: dto.credential,
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const traditionalSignInAsyncAction = createAsyncThunk<
  IAuthResponse,
  ITraditionalSignInDto
>("user/traditionalSignIn", async (dto, { rejectWithValue }) => {
  try {
    const response = await api.put<IAuthResponse>(`/auth/sign-in/traditional`, {
      email: dto.email,
      password: dto.password,
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const setPasswordAsyncAction = createAsyncThunk<
  IAuthResponse,
  ISetPasswordDto
>("user/setPassword", async (dto, { rejectWithValue }) => {
  try {
    const response = await api.put(`/auth/set-password`, {
      currentPassword: dto.currentPassword,
      newPassword: dto.newPassword,
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const setupTwoFactorAsyncAction = createAsyncThunk<
  { qrCodeDataUrl: string; secret: string },
  void
>("user/setupTwoFactor", async (_, { rejectWithValue }) => {
  try {
    const response = await api.put<{ qrCodeDataUrl: string; secret: string }>(
      `/auth/2fa/setup`,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const verifyTwoFactorAsyncAction = createAsyncThunk<
  IAuthResponse,
  { code: string }
>("user/verifyTwoFactor", async (dto, { rejectWithValue }) => {
  try {
    const response = await api.put<IAuthResponse>(`/auth/2fa/verify`, {
      code: dto.code,
    });
    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setProfileAction: (state, action: PayloadAction<Partial<IUserProfile>>) => {
      const profile = action.payload;

      return {
        ...state,
        profile: {
          ...state.profile,
          ...profile,
        },
      };
    },
    signOutAction: (_) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        isAnyOf(
          verifyTokenAsyncAction.fulfilled,
          googleSignInAsyncAction.fulfilled,
          traditionalSignInAsyncAction.fulfilled,
          verifyTwoFactorAsyncAction.fulfilled,
          setPasswordAsyncAction.fulfilled,
        ),
        (state, action) => {
          const payload = action.payload as IAuthResponse | undefined;

          const { profile } = payload || {};

          return {
            ...state,
            profile: {
              ...state.profile,
              ...profile,
            },
            isLoading: false,
            isAuthenticated: true,
          };
        },
      )

      .addMatcher(
        isAnyOf(
          verifyTokenAsyncAction.rejected,
          googleSignInAsyncAction.rejected,
          traditionalSignInAsyncAction.rejected,
          verifyTwoFactorAsyncAction.rejected,
          setPasswordAsyncAction.rejected,
        ),
        (state) => {
          return {
            ...state,
            isLoading: false,
            isAuthenticated: false,
          };
        },
      )

      .addMatcher(
        isAnyOf(
          verifyTokenAsyncAction.pending,
          googleSignInAsyncAction.pending,
          traditionalSignInAsyncAction.pending,
          verifyTwoFactorAsyncAction.pending,
          setPasswordAsyncAction.pending,
        ),
        (state) => ({
          ...state,
          isLoading: true,
        }),
      );
  },
});

export const { setProfileAction, signOutAction } = userSlice.actions;

export default userSlice.reducer;
